import { useAsyncFn } from 'react-use';
import { Form, Formik } from 'formik';
import { Grid, TextField, Button, Checkbox, DialogActions, FormHelperText, FormControlLabel } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  AIFoundrySelector,
  ClusterSelector,
  clusterSelectorStyles,
  GithubOrgPrefix,
  NamespaceSelector,
  type GithubOrigin,
} from '@runway/devkit';
import { Progress } from '@backstage/core-components';
import * as Yup from 'yup';
import type { AIFoundrySelectorOutput } from '@runway/devkit';

export type Props = {
  appName: string;
  hideResult: boolean;
  handleClose: () => void;
  submitState: ReturnType<typeof useAsyncFn>[0];
  doSubmit: (values: any) => Promise<string>;
  entityOrg: string;
  isEmuEnabled: boolean;
  onOrganizationChange: (organization: string, origin: GithubOrigin) => void;
  isAIAgentService: boolean;
  aiConfigData: Record<string, unknown> | null;
  aiConfigLoading: boolean;
  aiConfigError?: string;
};

export type RegisterAppFormValues = {
  hostCluster: string;
  namespace: string;
  appName: string;
  sourcePath: string;
  sourceRepo: string;
  createAIInfrastructure: boolean;
  aiFoundrySelection: AIFoundrySelectorOutput | null;
};

export const RegisterAppForm = ({
  appName,
  hideResult,
  handleClose,
  submitState,
  doSubmit,
  entityOrg,
  isEmuEnabled,
  onOrganizationChange,
  isAIAgentService,
  aiConfigData,
  aiConfigLoading,
  aiConfigError,
}: Props) => {
  const classes = clusterSelectorStyles();

  const argoFormSchema = Yup.object().shape({
    hostCluster: Yup.string().required(),
    namespace: Yup.string().required(),
    appName: Yup.string()
      .required()
      .matches(/^[a-z0-9-]*$/g, 'Must not contain uppercase letters or underscores'),
    sourcePath: Yup.string().required(),
    sourceRepo: Yup.string().required().matches(/^\S*$/g, 'Source Repo cannot contain spaces'),
    aiFoundrySelection: Yup.mixed().when('createAIInfrastructure', {
      is: (createAIInfrastructure: boolean) => isAIAgentService && createAIInfrastructure,
      then: schema => schema.required('AI Foundry is required for AI Agent deployments'),
      otherwise: schema => schema.nullable(),
    }),
  });

  return (
    <Formik
      validationSchema={argoFormSchema}
      validateOnMount
      initialValues={{
        hostCluster: '',
        namespace: '',
        appName: appName,
        sourcePath: 'k8s/nonprod',
        sourceRepo: appName,
        createAIInfrastructure: false,
        aiFoundrySelection: null,
      }}
      onSubmit={values => {
        doSubmit(values);
      }}
    >
      {formik => (
        <Form>
          {isAIAgentService && formik.values.createAIInfrastructure && aiConfigError && (
            <Alert severity="warning" aria-label="ai-config-warning">
              {aiConfigError}
            </Alert>
          )}
          <Grid container spacing={4} classes={{ root: classes.container }}>
            <Grid item>
              <ClusterSelector
                onClick={(cluster: string) => {
                  const previousEnvironment = formik.values.hostCluster.includes('-p-') ? 'prod' : 'nonprod';
                  const nextEnvironment = cluster.includes('-p-') ? 'prod' : 'nonprod';
                  formik.setFieldValue('hostCluster', cluster);
                  formik.setFieldValue('appName', cluster.includes('-p-') ? `${appName}-prod` : `${appName}-nonprod`);
                  formik.setFieldValue('sourcePath', cluster.includes('-p-') ? `k8s/prod` : `k8s/nonprod`);
                  formik.setTouched({ appName: true });
                  formik.setTouched({ sourcePath: true });
                  if (previousEnvironment !== nextEnvironment) {
                    formik.setFieldValue('aiFoundrySelection', null);
                  }
                }}
                includeProd
              />
            </Grid>

            {isAIAgentService && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={formik.values.createAIInfrastructure}
                      onChange={event => {
                        const checked = event.target.checked;
                        formik.setFieldValue('createAIInfrastructure', checked);
                        if (!checked) {
                          formik.setFieldValue('aiFoundrySelection', null);
                        }
                      }}
                      inputProps={{ 'aria-label': 'Create AI Infrastructure' }}
                    />
                  }
                  label="Also create AI Infrastructure for this deployment"
                />
              </Grid>
            )}

            {isAIAgentService && formik.values.createAIInfrastructure && (
              <Grid item xs={7}>
                <AIFoundrySelector
                  onChange={selection => {
                    formik.setFieldValue('aiFoundrySelection', selection);
                  }}
                  selectedFoundry={
                    (formik.values.aiFoundrySelection as AIFoundrySelectorOutput | null)?.foundry ?? null
                  }
                  environment={formik.values.hostCluster.includes('-p-') ? 'prod' : 'nonprod'}
                  aiConfigData={aiConfigData}
                  disabled={!formik.values.hostCluster || aiConfigLoading}
                  label="Select AI Foundry Instance"
                />
                <FormHelperText>{formik.errors.aiFoundrySelection as string}</FormHelperText>
              </Grid>
            )}

            <Grid item xs={7}>
              <NamespaceSelector
                selectedCluster={formik.values.hostCluster}
                isCreateButtonVisible
                onChange={(values: any) => {
                  formik.setFieldValue('namespace', values);
                }}
              />
            </Grid>

            <Grid item xs={7}>
              <TextField
                required
                label="App Name"
                variant="outlined"
                id="appName"
                value={formik.values.appName}
                onChange={event => {
                  formik.setFieldValue('appName', event.target.value);
                }}
                onBlur={formik.handleBlur}
                error={Boolean(formik.errors.appName)}
                helperText={formik.errors.appName}
              />
            </Grid>

            <Grid item xs={7}>
              <TextField
                required
                label="Source Repo"
                variant="outlined"
                id="githubpath"
                defaultValue={appName}
                onChange={event => {
                  formik.setFieldValue('sourceRepo', event.target.value);
                }}
                onBlur={formik.handleBlur}
                error={Boolean(formik.errors.sourceRepo)}
                helperText={formik.errors.sourceRepo || `Name of your ${entityOrg} repo with the k8s yaml file.`}
                InputProps={{
                  startAdornment: (
                    <GithubOrgPrefix
                      organization={entityOrg}
                      selectable={isEmuEnabled}
                      onOrganizationChange={onOrganizationChange}
                    />
                  ),
                }}
              />
            </Grid>

            <Grid item xs={7}>
              <TextField
                required
                label="Source Path"
                variant="outlined"
                id="k8path"
                value={formik.values.sourcePath}
                onChange={event => {
                  formik.setFieldValue('sourcePath', event.target.value);
                }}
                onBlur={formik.handleBlur}
                helperText={formik.errors.sourcePath}
              />
              <FormHelperText>directory of k8s config yaml file</FormHelperText>
            </Grid>
          </Grid>

          {submitState.loading && <Progress />}

          {!hideResult && submitState.error && (
            <Alert severity="error" aria-label="error">
              {submitState.error.message}
            </Alert>
          )}

          <DialogActions>
            <Button
              type="button"
              className="outline"
              onClick={() => {
                handleClose();
              }}
              disabled={submitState.loading}
            >
              Close
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={!formik.isValid || submitState.loading}>
              Submit
            </Button>
          </DialogActions>
        </Form>
      )}
    </Formik>
  );
};
